import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Check, ArrowRight, ChevronRight, Search, Smartphone, ClipboardList, Settings, Share2, Globe, MessageSquare, LayoutGrid, FolderOpen, Pen, Monitor, CheckCircle, Send, Headphones, PawPrint, Camera, Image, FileText, Megaphone, Scissors, Shirt, Coffee, ShoppingBag, CreditCard, Flag, Car, UserPlus, Database, Type, ListPlus, ImageIcon, Star, Compass, Wrench, Package, Eye } from "lucide-react";
import { WhatsAppIcon } from "@/components/PageCTA";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
};

/* ═══ SHARED ═══ */
const HeroBg = ({ eyebrow = "Usługa", serviceName, title, desc, sub, cta1, cta1Link = "/kontakt", cta2, cta2Link = "/realizacje", img = "/images/hero-mockup.jpg" }) => (
  <div className="subpage-hero subpage-hero--bg flex items-end" style={{ backgroundImage: `linear-gradient(90deg, rgba(10,10,9,.98) 0%, rgba(10,10,9,.90) 38%, rgba(10,10,9,.25) 72%, rgba(10,10,9,.08) 100%), url(${img})` }}>
    <div className="container pb-0">
      <p className="eyebrow mb-3">{eyebrow}</p>
      {serviceName && <h1 className="font-heading text-[clamp(40px,5.5vw,64px)] text-[var(--color-text-light)] mb-2">{serviceName}</h1>}
      <p className="font-script text-xl text-[var(--color-gold)] mb-4">{title}</p>
      {sub && <p className="font-body text-[var(--color-text-light-muted)] text-base max-w-lg mb-4 leading-relaxed">{sub}</p>}
      <p className="font-body text-sm text-[var(--color-text-light-muted)] mb-8 max-w-lg">{desc}</p>
      <div className="flex flex-wrap gap-4">
        {cta1 && <Link to={cta1Link}><button className="button-primary">{cta1} <ArrowRight className="w-4 h-4" /></button></Link>}
        {cta2 && <Link to={cta2Link}><button className="button-outline">{cta2}</button></Link>}
      </div>
    </div>
  </div>
);

const FeatureGrid = ({ title, items }) => (
  <FadeIn>
    <h2 className="font-heading text-[clamp(26px,3vw,36px)] text-[var(--color-text-dark)] mb-10 italic">{title}</h2>
    <div className="grid md:grid-cols-3 gap-6">
      {items.map((item, idx) => {
        const Icon = item.icon;
        return (
          <div key={idx} className="flex gap-4 p-1">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
              <Icon className="w-7 h-7 text-[var(--color-gold)]" strokeWidth={1.3} />
            </div>
            <div>
              <h3 className="font-body text-xs font-bold uppercase tracking-wider text-[var(--color-text-dark)] mb-1.5">{item.t}</h3>
              <p className="text-xs font-body text-[var(--color-text-muted)] leading-relaxed">{item.d}</p>
            </div>
          </div>
        );
      })}
    </div>
  </FadeIn>
);

const ProcessSection = ({ title, steps }) => (
  <FadeIn delay={0.1}>
    <h2 className="font-heading text-[clamp(26px,3vw,36px)] text-[var(--color-text-dark)] mb-8 italic">{title}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {steps.map((step, idx) => {
        const Icon = step.icon;
        return (
          <div key={idx} className="relative bg-[var(--color-white)] border border-[var(--color-border-light)] rounded p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-heading text-2xl font-medium text-[var(--color-gold)]">{String(idx + 1).padStart(2, '0')}</span>
              <Icon className="w-6 h-6 text-[var(--color-gold)]" strokeWidth={1.3} />
            </div>
            <h3 className="font-body text-sm font-bold text-[var(--color-text-dark)] mb-2">{step.t}</h3>
            <p className="text-xs font-body text-[var(--color-text-muted)] leading-relaxed">{step.d}</p>
            {idx < steps.length - 1 && (
              <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-gold)] z-10" />
            )}
          </div>
        );
      })}
    </div>
  </FadeIn>
);

const ForWhom = ({ title = "Dla kogo?", desc }) => (
  <FadeIn delay={0.2}>
    <div className="bg-[var(--color-charcoal)] rounded p-10">
      <h2 className="font-heading text-2xl font-medium text-[var(--color-text-light)] mb-4 italic">{title}</h2>
      <p className="text-sm font-body text-[var(--color-text-light-muted)] leading-relaxed max-w-2xl">{desc}</p>
    </div>
  </FadeIn>
);

const EndCTA = ({ title, desc, btn = "Zapytaj o wycenę" }) => (
  <div className="bg-[var(--color-black-soft)]" style={{ paddingBlock: 80 }}>
    <div className="container text-center max-w-2xl mx-auto">
      <FadeIn><h2 className="font-heading text-[clamp(28px,3.5vw,42px)] text-[var(--color-text-light)] mb-4">{title}</h2></FadeIn>
      <FadeIn delay={0.1}><p className="font-body text-[var(--color-text-light-muted)] text-sm mb-8 leading-relaxed">{desc}</p></FadeIn>
      <FadeIn delay={0.15}>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/kontakt"><button className="button-primary">{btn} <ArrowRight className="w-4 h-4" /></button></Link>
          <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer"><button className="button-outline flex items-center gap-2"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</button></a>
        </div>
      </FadeIn>
    </div>
  </div>
);

/* ═══════════════════════════
   1. STRONY WWW
   ═══════════════════════════ */
const WebPage = () => (
  <div data-testid="service-page-web">
    <HeroBg serviceName="Strony WWW" title="Strony WWW, które sprzedają" sub="Projektujemy nowoczesne strony internetowe, które nie tylko dobrze wyglądają, ale przede wszystkim pomagają zdobywać klientów, prezentować ofertę i budować profesjonalny wizerunek marki." desc="Projekt, wdrożenie, SEO i formularze kontaktowe — wszystko w jednym miejscu." cta1="Darmowa wycena" cta2="Zobacz realizacje" />
    <div className="section bg-[var(--color-cream)]"><div className="container space-y-20">
      <FeatureGrid title="Co otrzymujesz" items={[
        { icon: Search, t: "SEO i szybkość", d: "Strona przygotowana tak, aby była czytelna dla użytkowników i wyszukiwarki Google." },
        { icon: Smartphone, t: "Wersja mobilna", d: "Projekt dopasowany do telefonów, tabletów i komputerów." },
        { icon: ClipboardList, t: "Formularze kontaktowe", d: "Tworzymy formularze zapytań, rezerwacji i kontaktu." },
        { icon: Settings, t: "Panel administracyjny", d: "Możesz samodzielnie aktualizować treści, zdjęcia, psy, ofertę lub aktualności." },
        { icon: Share2, t: "Integracje", d: "Google Analytics, Meta Pixel, WhatsApp, Messenger, mapa Google i media społecznościowe." },
        { icon: Globe, t: "Wersje językowe", d: "Przygotowujemy strony w języku polskim, angielskim, niemieckim i innych wersjach językowych." },
      ]} />
      <ProcessSection title="Jak zaczynamy projekt" steps={[
        { icon: MessageSquare, t: "Rozmowa i wywiad", d: "Poznajemy Twoją markę, cele strony, zakres treści i potrzebne funkcje." },
        { icon: LayoutGrid, t: "Zakres i struktura", d: "Ustalamy układ strony, podstrony, sekcje i najważniejsze elementy." },
        { icon: FolderOpen, t: "Materiały", d: "Zdjęcia, logo, opisy, dane kontaktowe i materiały potrzebne do startu." },
        { icon: Pen, t: "Koncepcja graficzna", d: "Dobieramy układ, typografię, kolory i styl dopasowany do marki." },
      ]} />
      <ProcessSection title="Jak pracujemy" steps={[
        { icon: Monitor, t: "Projekt i wdrożenie", d: "Budujemy stronę, wdrażamy sekcje, formularze i wersję mobilną." },
        { icon: CheckCircle, t: "Poprawki i testy", d: "Sprawdzamy działanie strony, linków, formularzy i wygląd na urządzeniach." },
        { icon: Send, t: "Publikacja", d: "Uruchamiamy stronę pod właściwą domeną." },
        { icon: Headphones, t: "Wsparcie po wdrożeniu", d: "Pomagamy w aktualizacjach, rozbudowie i bieżących zmianach." },
      ]} />
      <div>
        <FadeIn><h2 className="font-heading text-[clamp(26px,3vw,36px)] text-[var(--color-text-dark)] mb-8 italic">Rodzaje stron</h2></FadeIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { t: "Strona dla hodowli", d: "Prezentacja hodowli, psów, miotów, galerii i formularza kontaktowego.", img: "/images/products/poster.png" },
            { t: "Strona reproduktora", d: "Profesjonalna prezentacja psa, badań, osiągnięć i warunków krycia.", img: "/images/portfolio/branding-set.png" },
            { t: "Landing page", d: "Jedna konkretna strona pod reklamę, usługę, wydarzenie lub miot.", img: "/images/portfolio/rollup.png" },
            { t: "Strona firmowa", d: "Dla groomerów, trenerów, hoteli dla zwierząt i marek z branży zoologicznej.", img: "/images/portfolio/banner.png" },
          ].map((card, idx) => (
            <FadeIn key={idx} delay={idx * 0.06}>
              <div className="overflow-hidden rounded border border-[var(--color-border-light)]">
                <div className="aspect-[4/3] overflow-hidden"><img src={card.img} alt={card.t} className="w-full h-full object-cover" /></div>
                <div className="p-4 bg-[var(--color-white)]">
                  <h3 className="font-body text-xs font-bold uppercase tracking-wider text-[var(--color-text-dark)] mb-1">{card.t}</h3>
                  <p className="text-xs font-body text-[var(--color-text-muted)]">{card.d}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </div></div>
    <EndCTA title="Stwórzmy stronę dopasowaną do Twojej marki" desc="Każdy projekt traktujemy indywidualnie. Podczas rozmowy poznajemy Twoje potrzeby, doradzamy najlepsze rozwiązania i przygotujemy przejrzystą wycenę." />
  </div>
);

/* ═══════════════════════════
   2. GRAFIKI HODOWLANE
   ═══════════════════════════ */
const GraphicsPage = () => (
  <div data-testid="service-page-graphics">
    <HeroBg serviceName="Grafiki hodowlane" title="Grafiki, które przyciągają uwagę" sub="Tworzymy indywidualne grafiki dla hodowli, właścicieli reproduktorów i firm z branży zoologicznej." desc="Każdy projekt dopasowujemy do charakteru hodowli, rasy, kolorystyki oraz miejsca publikacji." cta1="Zamów grafikę" cta2="Zobacz realizacje" img="/images/hero-bg.jpg" />
    <div className="section bg-[var(--color-cream)]"><div className="container space-y-20">
      <FeatureGrid title="Co możemy przygotować" items={[
        { icon: PawPrint, t: "Zapowiedzi miotów", d: "Profesjonalne zapowiedzi skojarzeń i miotów, które budzą zainteresowanie." },
        { icon: Eye, t: "Prezentacje psów", d: "Indywidualne prezentacje psów i reproduktorów podkreślające ich atuty." },
        { icon: Image, t: "Dostępne szczenięta", d: "Estetyczne grafiki promujące szczenięta i ułatwiające znalezienie nowych domów." },
        { icon: Megaphone, t: "Social media", d: "Grafiki dopasowane do mediów społecznościowych Twojej hodowli." },
        { icon: Flag, t: "Plakaty wystawowe", d: "Plakaty i materiały wystawowe w stylu podkreślającym klasę psów." },
        { icon: Scissors, t: "Obróbka zdjęć", d: "Retusz i obróbka zdjęć psów na potrzeby grafik i publikacji." },
      ]} />
      <ProcessSection title="Jak pracujemy" steps={[
        { icon: FolderOpen, t: "Materiały i ustalenia", d: "Otrzymujemy zdjęcia, treści, logo oraz informacje o oczekiwanym stylu." },
        { icon: Pen, t: "Projekt", d: "Tworzymy koncepcję graficzną i projekt, który oddaje charakter Twojej hodowli i rasy." },
        { icon: CheckCircle, t: "Korekta", d: "Wprowadzamy uzgodnione poprawki — aż będzie w 100% zgodny z Twoimi oczekiwaniami." },
        { icon: Package, t: "Gotowe pliki", d: "Otrzymujesz gotowe pliki w odpowiednich formatach, gotowe do publikacji." },
      ]} />
      <ForWhom desc="Dla hodowców szukających profesjonalnych ogłoszeń, zapowiedzi miotów, prezentacji psów i materiałów do social media." />
    </div></div>
    <EndCTA title="Pokaż swoją hodowlę w profesjonalnej oprawie" desc="Prześlij zdjęcia i najważniejsze informacje. Przygotujemy grafikę dopasowaną do Twojego psa, miotu lub wydarzenia." btn="Zamów projekt" />
  </div>
);

/* ═══════════════════════════
   3. LOGO I BRANDING
   ═══════════════════════════ */
const LogoPage = () => (
  <div data-testid="service-page-logo">
    <HeroBg serviceName="Logo i branding" title="Logo, które buduje rozpoznawalność" sub="Projektujemy logo i identyfikację wizualną dla hodowli, groomerów, trenerów, hoteli dla zwierząt oraz marek z branży zoologicznej." desc="Tworzymy znaki, które oddają charakter marki, są czytelne i dobrze wyglądają zarówno w internecie, jak i na materiałach drukowanych." cta1="Zamów logo" cta2="Zobacz projekty" img="/images/hero-bg.jpg" />
    <div className="section bg-[var(--color-cream)]"><div className="container space-y-20">
      <FeatureGrid title="Co obejmuje oferta" items={[
        { icon: Pen, t: "Projekt logo", d: "Indywidualny znak dopasowany do nazwy, charakteru marki, rasy oraz oczekiwanego stylu." },
        { icon: Eye, t: "Odświeżenie logo", d: "Uporządkowanie istniejącego znaku, poprawa proporcji, typografii i czytelności." },
        { icon: Compass, t: "Kolorystyka marki", d: "Dobór spójnej palety kolorów do strony, social mediów, materiałów reklamowych i druku." },
        { icon: Type, t: "Typografia", d: "Dobór fontów, które podkreślają styl marki i zapewniają spójność wszystkich materiałów." },
        { icon: Image, t: "Wersje logo", d: "Warianty jasne, ciemne, monochromatyczne, poziome, pionowe oraz z przezroczystym tłem." },
        { icon: CreditCard, t: "Materiały firmowe", d: "Wizytówki, papier firmowy, podpis e-mail, grafiki profilowe i elementy social mediów." },
      ]} />
      <ProcessSection title="Jak pracujemy" steps={[
        { icon: MessageSquare, t: "Brief", d: "Poznajemy nazwę, charakter marki, rasę, grupę klientów i preferowany styl." },
        { icon: Pen, t: "Koncepcja", d: "Przygotowujemy kierunek wizualny dopasowany do marki." },
        { icon: CheckCircle, t: "Dopracowanie", d: "Ustalamy typografię, proporcje, kolory i szczegóły znaku." },
        { icon: Package, t: "Pakiet plików", d: "Przekazujemy logo w formatach przeznaczonych do internetu i druku." },
      ]} />
      <ForWhom desc="Dla hodowców, groomerów, trenerów, hoteli dla zwierząt, fotografów i marek z branży zoo, które chcą profesjonalnej identyfikacji wizualnej." />
    </div></div>
    <EndCTA title="Zbudujmy spójny wizerunek Twojej marki" desc="Dobre logo to początek rozpoznawalnej identyfikacji. Opowiedz nam o swojej hodowli lub firmie." btn="Zapytaj o projekt logo" />
  </div>
);

/* ═══════════════════════════
   4. MATERIAŁY REKLAMOWE
   ═══════════════════════════ */
const MaterialsPage = () => (
  <div data-testid="service-page-apparel">
    <HeroBg serviceName="Materiały reklamowe" title="Twoja marka widoczna także poza internetem" sub="Projektujemy i przygotowujemy materiały reklamowe z logo, nazwą hodowli, wizerunkiem psa lub indywidualną grafiką." desc="Dbamy o to, aby wszystkie elementy były spójne ze stroną internetową i identyfikacją wizualną marki." cta1="Zamów materiały" cta2="Zapytaj o wycenę" cta2Link="/kontakt" img="/images/hero-bg.jpg" />
    <div className="section bg-[var(--color-cream)]"><div className="container space-y-20">
      <FeatureGrid title="Co możemy przygotować" items={[
        { icon: Shirt, t: "Odzież z nadrukiem", d: "Koszulki, bluzy, polary i inne elementy odzieży z Twoim projektem." },
        { icon: Coffee, t: "Kubki i gadżety", d: "Kubki, smycze, breloczki i inne gadżety z logo lub grafiką hodowli." },
        { icon: ShoppingBag, t: "Torby bawełniane", d: "Torby z nadrukiem — praktyczne i estetyczne, idealne na eventy na co dzień." },
        { icon: CreditCard, t: "Wizytówki i druk", d: "Wizytówki, ulotki, foldery i inne materiały drukowane dopasowane do marki." },
        { icon: Flag, t: "Banery reklamowe", d: "Banery, roll-upy i siatki reklamowe na wystawy, eventy i zawody." },
        { icon: Car, t: "Reklama na samochodzie", d: "Projekt i przygotowanie grafiki na samochody — skuteczna reklama w ruchu." },
      ]} />
      <ProcessSection title="Jak pracujemy" steps={[
        { icon: MessageSquare, t: "Ustalenia", d: "Poznajemy Twoją markę, potrzeby i cel. Ustalamy zakres materiałów oraz preferencje projektowe." },
        { icon: Pen, t: "Projekt", d: "Tworzymy projekty dopasowane do identyfikacji wizualnej i przedstawiamy do akceptacji." },
        { icon: Wrench, t: "Produkcja", d: "Po akceptacji przygotowujemy pliki do druku lub realizujemy zamówienie u sprawdzonych dostawców." },
        { icon: Package, t: "Gotowe pliki", d: "Dostarczamy gotowe materiały lub organizujemy wysyłkę bezpośrednio do Ciebie." },
      ]} />
      <ForWhom desc="Dla hodowców, klubów kynologicznych, organizatorów eventów i firm, które chcą prezentować swoją markę na materiałach reklamowych." />
    </div></div>
    <EndCTA title="Wyróżnij swoją markę na wystawie, wydarzeniu i każdego dnia" desc="Przygotujemy materiały dopasowane do Twojej hodowli, firmy i sposobu promocji." btn="Zapytaj o realizację" />
  </div>
);

/* ═══════════════════════════
   5. WIZYTÓWKA GOOGLE
   ═══════════════════════════ */
const GooglePage = () => (
  <div data-testid="service-page-google">
    <HeroBg serviceName="Wizytówka Google" title="Bądź widoczny tam, gdzie szukają Cię klienci" sub="Profesjonalnie przygotowany Profil Firmy w Google pomaga klientom znaleźć Twoją działalność w wyszukiwarce i Mapach Google." desc="To ważne narzędzie dla firm działających lokalnie: groomerów, trenerów, hoteli dla zwierząt, fotografów, sklepów i usługodawców." cta1="Zamów konfigurację" cta2="Sprawdź ofertę" cta2Link="/pakiety" img="/images/hero-bg.jpg" />
    <div className="section bg-[var(--color-cream)]"><div className="container space-y-20">
      <FeatureGrid title="Co obejmuje usługa" items={[
        { icon: UserPlus, t: "Założenie profilu", d: "Tworzymy lub przejmujemy Twój Profil Firmy w Google, aby w pełni wykorzystać jego potencjał." },
        { icon: Database, t: "Uzupełnienie danych", d: "Dodajemy wszystkie kluczowe informacje: dane kontaktowe, lokalizację, godziny otwarcia i kategorię." },
        { icon: FileText, t: "Profesjonalny opis", d: "Piszemy angażujący opis Twojej firmy, który buduje zaufanie i zachęca do kontaktu." },
        { icon: ListPlus, t: "Dodanie usług", d: "Dodajemy listę usług lub produktów, aby klienci szybko znaleźli to, czego potrzebują." },
        { icon: ImageIcon, t: "Zdjęcia i materiały", d: "Dodajemy zdjęcia i materiały wizualne, które wyróżniają Twoją firmę w wynikach wyszukiwania." },
        { icon: Star, t: "Opinie klientów", d: "Pomagamy w zarządzaniu opiniami i budowaniu pozytywnych ocen, które zwiększają zaufanie." },
      ]} />
      <ProcessSection title="Jak pracujemy" steps={[
        { icon: Search, t: "Analiza", d: "Sprawdzamy, czy profil już istnieje i czy dane są poprawne. Analizujemy konkurencję i potencjał lokalny." },
        { icon: Settings, t: "Konfiguracja", d: "Uzupełniamy najważniejsze informacje, kategorie, opis i usługi. Optymalizujemy profil pod wyszukiwarkę i Mapy Google." },
        { icon: Camera, t: "Materiały", d: "Dodajemy zdjęcia i porządkujemy prezentację marki. Tworzymy spójny, profesjonalny wizerunek." },
        { icon: MessageSquare, t: "Wskazówki", d: "Przekazujemy zalecenia dotyczące aktualizacji, publikacji i opinii, aby profil rozwijał się na Twoje korzyści." },
      ]} />
      <ForWhom desc="Dla salonów groomerskich, trenerów, hoteli dla zwierząt, fotografów, lekarzy weterynarii, sklepów zoologicznych i lokalnych firm z branży pet." />
    </div></div>
    <EndCTA title="Pomóż klientom szybciej znaleźć Twoją firmę" desc="Uporządkujemy Profil Firmy w Google i zadbamy, aby Twoja marka prezentowała się profesjonalnie." btn="Zamów usługę" />
  </div>
);

/* ═══ ROUTER ═══ */
const ServicePage = ({ service }) => {
  switch (service) {
    case "web": return <WebPage />;
    case "graphics": return <GraphicsPage />;
    case "logo": return <LogoPage />;
    case "apparel": return <MaterialsPage />;
    case "google": return <GooglePage />;
    default: return <div className="min-h-screen bg-[var(--color-black)] flex items-center justify-center"><p className="text-[var(--color-text-light)]">Usługa nie znaleziona</p></div>;
  }
};

export default ServicePage;
